import {
  API_PREFIX,
  apiErrorSchema,
  type BodyOf,
  buildPath,
  buildQuery,
  carriesSession,
  CSRF_HEADER,
  type ErrorCode,
  type QueryOf,
  type ResponseOf,
  type RouteDef,
} from '@pmai/contracts';

/**
 * The only way the web app talks to the API (engineering.md §3: no direct `fetch`). Paths, bodies
 * and responses are typed by the route catalogue in `@pmai/contracts`; responses are validated
 * against the same zod schemas the API uses.
 */

export type ClientErrorCode = ErrorCode | 'NETWORK';

export class ApiError extends Error {
  constructor(
    readonly code: ClientErrorCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level messages from a VALIDATION_FAILED response (`details.issues`). */
  get fieldIssues(): { path: string; message: string }[] {
    const issues = (this.details as { issues?: unknown } | undefined)?.issues;
    return Array.isArray(issues) ? (issues as { path: string; message: string }[]) : [];
  }
}

export interface CallOptions<R extends RouteDef> {
  params?: Record<string, string>;
  body?: BodyOf<R>;
  query?: QueryOf<R>;
  /** A 401 is an expected answer here (e.g. "am I signed in?") — do not redirect to login. */
  anonymousOk?: boolean;
}

let csrfToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

/** The session's CSRF token arrives with every `Me` payload (login, me, switch, accept). */
export const setCsrfToken = (token: string | null): void => {
  csrfToken = token;
};

/** FR-AUTH-005: a 401 on any request (session expired) sends the user to the login page. */
export const setUnauthenticatedHandler = (handler: () => void): void => {
  onUnauthenticated = handler;
};

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function call<R extends RouteDef>(route: R, opts: CallOptions<R> = {}): Promise<ResponseOf<R>> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (MUTATING.has(route.method) && csrfToken) headers[CSRF_HEADER] = csrfToken;

  let res: Response;
  try {
    res = await fetch(API_PREFIX + buildPath(route.path, opts.params) + buildQuery(opts.query as object), {
      method: route.method,
      headers,
      credentials: 'same-origin',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiError('NETWORK', 'Network error', 0);
  }

  const json: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(json);
    const error = parsed.success
      ? new ApiError(parsed.data.error.code, parsed.data.error.message, res.status, parsed.data.error.details)
      : // No envelope: the API itself is unreachable behind the proxy (FR-AUTH-004).
        new ApiError(
          res.status >= 502 && res.status <= 504 ? 'NETWORK' : 'INTERNAL',
          `HTTP ${res.status}`,
          res.status,
        );
    if (error.code === 'UNAUTHENTICATED' && !opts.anonymousOk) onUnauthenticated?.();
    throw error;
  }

  const body = (route.response ? route.response.parse(json) : json) as ResponseOf<R>;
  if (carriesSession(body)) setCsrfToken(body.csrfToken);
  return body;
}
